/**
 * Chat UI slice — sidebar visibility, per-model cooldowns, and the URL a new
 * chat was promoted to after its first send.
 *
 * The only chat-related Redux state ChatV2 keeps; everything else about a chat
 * lives in the module's own registries.
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

/** Per-model rate-limit info. */
export interface CoolDownModelInfo {
  modelId: string;
  availableAt: string;
  availableTime: Date;
  modelTier: string;
}

/**
 * A new chat's URL swap (`/chat` to `/chat/:id`). The chat view rewrites the address
 * with `history.replaceState`, which React Router never observes, so the router's
 * location keeps its pre-swap `key`. `locationKey` ties the swap to that location;
 * any later navigation has a different key and makes this record inert.
 */
export interface PromotedChatUrl {
  locationKey: string;
  path: string;
}

export interface ChatUiState {
  showChatHistoryPanel: boolean;
  coolDownModels: Record<string, Omit<CoolDownModelInfo, 'modelId'>>;
  promotedChatUrl: PromotedChatUrl | null;
}

const initialState: ChatUiState = {
  showChatHistoryPanel: true,
  coolDownModels: {},
  promotedChatUrl: null
};

const slice = createSlice({
  name: 'chatUi',
  initialState,
  reducers: {
    toggleChatHistoryPanel(state) {
      state.showChatHistoryPanel = !state.showChatHistoryPanel;
    },
    setChatHistoryPanel(state, action: PayloadAction<boolean>) {
      state.showChatHistoryPanel = action.payload;
    },
    addCoolDownModel(state, action: PayloadAction<CoolDownModelInfo>) {
      const { modelId, availableAt, availableTime, modelTier } = action.payload;
      state.coolDownModels[modelId] = { availableAt, availableTime, modelTier };
    },
    removeCoolDownModel(state, action: PayloadAction<string>) {
      delete state.coolDownModels[action.payload];
    },
    setCoolDownModels(
      state,
      action: PayloadAction<Record<string, Omit<CoolDownModelInfo, 'modelId'>>>
    ) {
      state.coolDownModels = action.payload;
    },
    setPromotedChatUrl(state, action: PayloadAction<PromotedChatUrl>) {
      state.promotedChatUrl = action.payload;
    }
  }
});

export const {
  toggleChatHistoryPanel,
  setChatHistoryPanel,
  addCoolDownModel,
  removeCoolDownModel,
  setCoolDownModels,
  setPromotedChatUrl
} = slice.actions;

export const reducer = slice.reducer;

export default slice;
